import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assessmentAPI } from '../lib/api';
import {
  ArrowLeft,
  Shield,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileCheck,
  ChevronRight,
  TrendingUp,
} from 'lucide-react';

const statusConfig = {
  'audit-ready': { label: 'Audit Ready', color: 'emerald', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: CheckCircle2 },
  'on-track': { label: 'On Track', color: 'amber', bg: 'bg-amber-500/15', border: 'border-amber-500/30', text: 'text-amber-400', icon: TrendingUp },
  'needs-attention': { label: 'Needs Attention', color: 'red', bg: 'bg-red-500/15', border: 'border-red-500/30', text: 'text-red-400', icon: AlertTriangle },
};

function RadialProgress({ percent, size = 120, stroke = 8, color = '#8b5cf6' }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(100,116,139,0.15)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        className="transition-all duration-700 ease-out" />
    </svg>
  );
}

function StatCard({ label, value, total, percent, icon: Icon, color }) {
  const colors = {
    violet: { bg: 'bg-violet-500/10', border: 'border-violet-500/20', text: 'text-violet-400', bar: 'bg-violet-500' },
    emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', bar: 'bg-emerald-500' },
    cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-400', bar: 'bg-cyan-500' },
    amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', bar: 'bg-amber-500' },
  };
  const c = colors[color] || colors.violet;
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`} className={`${c.bg} border ${c.border} rounded-xl p-5`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${c.text}`} />
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</span>
      </div>
      <div className="flex items-end justify-between">
        <div>
          <span className="text-3xl font-bold text-white">{percent}</span>
          <span className="text-lg text-slate-500">%</span>
        </div>
        <span className="text-sm text-slate-500">{value}/{total}</span>
      </div>
      <div className="mt-3 h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full ${c.bar} rounded-full transition-all duration-700`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function DomainRow({ domain, onNavigate }) {
  const cfg = statusConfig[domain.status];
  const StatusIcon = cfg.icon;
  return (
    <div
      data-testid={`domain-${domain.domain.toLowerCase().replace(/\s/g, '-')}`}
      className={`${cfg.bg} border ${cfg.border} rounded-xl p-5 hover:bg-opacity-25 transition-all cursor-pointer group`}
      onClick={() => onNavigate(domain.domain)}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <StatusIcon className={`w-5 h-5 ${cfg.text}`} />
          <h3 className="text-lg font-semibold text-white">{domain.domain}</h3>
          <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
            {cfg.label}
          </span>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-white transition-colors" />
      </div>
      <div className="grid grid-cols-4 gap-4">
        <div>
          <div className="text-xs text-slate-500 mb-1">Controls</div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-white">{domain.readinessPercent}%</span>
            <span className="text-xs text-slate-500">{domain.compliantControls}/{domain.totalControls}</span>
          </div>
          <div className="mt-1 h-1 bg-slate-800 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${domain.readinessPercent >= 80 ? 'bg-emerald-500' : domain.readinessPercent >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
              style={{ width: `${domain.readinessPercent}%` }} />
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">MUST Met</div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-white">{domain.mustPercent}%</span>
            <span className="text-xs text-slate-500">{domain.mustMet}/{domain.mustTotal}</span>
          </div>
          <div className="mt-1 h-1 bg-slate-800 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${domain.mustPercent >= 80 ? 'bg-emerald-500' : domain.mustPercent >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
              style={{ width: `${domain.mustPercent}%` }} />
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Evidence</div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-white">{domain.evidencePercent}%</span>
            <span className="text-xs text-slate-500">{domain.evidenceCollected}/{domain.evidenceTotal}</span>
          </div>
          <div className="mt-1 h-1 bg-slate-800 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${domain.evidencePercent >= 60 ? 'bg-emerald-500' : domain.evidencePercent >= 30 ? 'bg-amber-500' : 'bg-red-500'}`}
              style={{ width: `${domain.evidencePercent}%` }} />
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Breakdown</div>
          <div className="flex items-center gap-1.5 mt-1">
            <span className="text-xs text-emerald-400">{domain.compliantControls} Yes</span>
            <span className="text-slate-600">|</span>
            <span className="text-xs text-red-400">{domain.nonCompliant} No</span>
            {domain.notApplicable > 0 && (
              <>
                <span className="text-slate-600">|</span>
                <span className="text-xs text-slate-400">{domain.notApplicable} N/A</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ReadinessDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await assessmentAPI.getReadiness(id);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load readiness data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-red-400 text-center">
          <XCircle className="w-12 h-12 mx-auto mb-3" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const { overall, domains, priorityGaps, assessmentType, assessmentName } = data;

  const auditReady = domains.filter(d => d.status === 'audit-ready').length;
  const onTrack = domains.filter(d => d.status === 'on-track').length;
  const needsAttention = domains.filter(d => d.status === 'needs-attention').length;

  return (
    <div data-testid="readiness-dashboard" className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-to-assessment"
              onClick={() => navigate(`/assessment/${id}`)}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">Compliance Readiness</h1>
              <p className="text-sm text-slate-500">{assessmentName || assessmentType} Assessment</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${
              overall.readinessPercent >= 80 ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
              overall.readinessPercent >= 50 ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
              'bg-red-500/20 text-red-400 border border-red-500/30'
            }`}>
              {overall.readinessPercent}% Ready
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Overall Score Section */}
        <div className="grid grid-cols-5 gap-6">
          {/* Radial chart */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 flex flex-col items-center justify-center">
            <div className="relative">
              <RadialProgress
                percent={overall.readinessPercent}
                size={140}
                stroke={10}
                color={overall.readinessPercent >= 80 ? '#10b981' : overall.readinessPercent >= 50 ? '#f59e0b' : '#ef4444'}
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold text-white">{overall.readinessPercent}%</span>
                <span className="text-xs text-slate-500">Overall</span>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-slate-400">{auditReady} Ready</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-slate-400">{onTrack} On Track</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-slate-400">{needsAttention} Attention</span>
              </span>
            </div>
          </div>

          {/* Stat Cards */}
          <StatCard label="Controls Compliant" value={overall.compliant} total={overall.totalControls}
            percent={overall.readinessPercent} icon={Shield} color="violet" />
          <StatCard label="MUST Statements Met" value={overall.mustMet} total={overall.mustTotal}
            percent={overall.mustPercent} icon={CheckCircle2} color="emerald" />
          <StatCard label="Evidence Collected" value={overall.evidenceCollected} total={overall.evidenceTotal}
            percent={overall.evidencePercent} icon={FileCheck} color="cyan" />
          <StatCard label="Non-Compliant" value={overall.nonCompliant} total={overall.totalControls}
            percent={overall.totalControls > 0 ? Math.round((overall.nonCompliant / overall.totalControls) * 100) : 0}
            icon={XCircle} color="amber" />
        </div>

        {/* Domain Breakdown */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-4">Domain Readiness</h2>
          <div className="space-y-3">
            {domains.map(domain => (
              <DomainRow
                key={domain.domain}
                domain={domain}
                onNavigate={(domainName) => navigate(`/assessment/${id}?domain=${encodeURIComponent(domainName)}`)}
              />
            ))}
          </div>
        </div>

        {/* Priority Gaps */}
        {priorityGaps.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-white mb-4">Top Priority Gaps</h2>
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-red-500/10">
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Priority</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Control</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Domain</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Description</th>
                    <th className="text-center text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">MUST</th>
                    <th className="text-center text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {priorityGaps.map(gap => (
                    <tr
                      key={gap.questionId}
                      data-testid={`gap-${gap.questionId}`}
                      className="border-b border-slate-800/50 hover:bg-red-500/5 cursor-pointer transition-colors"
                      onClick={() => navigate(`/assessment/${id}?search=${gap.questionId}`)}
                    >
                      <td className="px-5 py-3">
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
                          P{gap.priorityNumber}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-sm font-mono text-slate-300">{gap.questionId}</td>
                      <td className="px-5 py-3 text-sm text-slate-400">{gap.domain}</td>
                      <td className="px-5 py-3 text-sm text-slate-300 max-w-md truncate">{gap.questionText}</td>
                      <td className="px-5 py-3 text-center text-sm text-slate-400">{gap.mustMet}/{gap.mustTotal}</td>
                      <td className="px-5 py-3 text-center text-sm text-slate-400">{gap.evidenceCollected}/{gap.evidenceTotal}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
